import { supabase } from "@/integrations/supabase/client";
import { buildAiChatFailureMessage, classifyAiChatFailure } from "./errorMessages";
import type { AiChatRole } from "./types";

export type AiChatHistoryMessage = {
  from: "agent" | "user";
  text: string;
};

export type AiChatResponse = {
  text: string;
  source: "gemini" | "system";
};

type AskAiChatInput = {
  question: string;
  history?: AiChatHistoryMessage[];
  role?: AiChatRole;
};

function buildSystemReply(text: string): AiChatResponse {
  return {
    text,
    source: "system",
  };
}

export async function askAiChat({ question, history = [], role = "guest" }: AskAiChatInput): Promise<AiChatResponse> {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000;
  void role;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke("ai-chat", {
        body: {
          question,
          role,
          history: history.slice(-10),
        },
      });

      if (error) {
        if (attempt < MAX_RETRIES && (error.message?.includes("fetch") || error.message?.includes("network"))) {
          console.warn(`AI chat attempt ${attempt} failed, retrying...`, error);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * attempt));
          continue;
        }
        return buildSystemReply(
          buildAiChatFailureMessage(
            classifyAiChatFailure({
              message: error.message,
            }),
          ),
        );
      }

      if (data?.error) {
        return buildSystemReply(
          buildAiChatFailureMessage(
            classifyAiChatFailure({
              status: typeof data?.status === "number" ? data.status : null,
              code: typeof data?.error_code === "string" ? data.error_code : null,
              message: typeof data?.error === "string" ? data.error : null,
            }),
          ),
        );
      }

      const responseText = typeof data?.text === "string" ? data.text.trim() : "";

      if (!responseText) {
        return buildSystemReply(buildAiChatFailureMessage("incomplete_response"));
      }

      return {
        text: responseText,
        source: "gemini",
      };
    } catch (err) {
      if (attempt === MAX_RETRIES) {
        console.warn("AI chat retries exhausted.", err);
        return buildSystemReply(buildAiChatFailureMessage("network_unavailable"));
      }
      console.warn(`AI chat attempt ${attempt} failed, retrying...`, err);
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * attempt));
    }
  }

  return buildSystemReply(buildAiChatFailureMessage("server_unavailable"));
}
